import { GridDefaults } from './Grid.constants';
import { AgGridReact } from 'ag-grid-react';
import React, { ReactNode, useState, useRef } from 'react';
import {
    CellClickedEvent,
    ColumnResizedEvent,
    FilterChangedEvent,
    FirstDataRenderedEvent,
    GridReadyEvent,
    RowClassParams,
    RowClickedEvent,
    RowSelectedEvent,
    RowStyle,
    SortChangedEvent,
} from 'ag-grid-community';
import Button from '@mui/material/Button';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EntitySettingsDrawer from '../entitySettings/entitySettingsDrawer';
import SettingsIcon from '@mui/icons-material/Settings';

export interface GridProps {
    gridStyle?: object;
    onGridReady?: (params: GridReadyEvent) => void;
    onColumnResized?: (params: ColumnResizedEvent) => void;
    domLayout?: string;
    testId?: string;
    immutableData?: boolean;
    tooltipShowDelay?: number;
    paginationPageSize?: number;
    getRowNodeId?: any;
    pagination?: boolean;
    rowModelType?: string;
    autofitColumns?: boolean;
    defaultColDef?: any;
    dataKey?: string;
    rowData?: any;
    rowHeight?: number;
    gridOptions?: any;
    gridClass?: string;
    rowStyle?: any;
    headerHeight?: number;
    onFirstDataRendered?: (event: FirstDataRenderedEvent) => void;
    onSortChanged?: (event: SortChangedEvent) => void;
    onFilterChanged?: (event: FilterChangedEvent) => void;
    onPaginationChanged?: (event: any) => void;
    frameworkComponents?: any;
    onCellClicked?: (event: CellClickedEvent) => void;
    onRowClicked?: (event: RowClickedEvent) => void;
    rowSelection?: string;
    onRowSelected?: (event: RowSelectedEvent) => void;
    masterDetail?: boolean;
    detailCellRendererParams?: any;
    getRowStyle?: (Params: RowClassParams) => RowStyle;
    suppressMenuHide?: boolean;
    children?: ReactNode;
    displayToolbar?: boolean;
    onCreateNew?: (event: any) => void;
    displayCreateNewButton?: boolean;
    groupIncludeTotalFooter?: boolean;
    alwaysShowHorizontalScroll?: boolean;
    displayEntitySettings?: boolean;
    settingKey?: string;
}

const Grid = (props: GridProps | any) => {
    const dataKey = props.dataKey;
    const gridRef = useRef<any>(null);
    const immutableData =
        props.immutableData || (dataKey && GridDefaults.immutableData);
    const [openSettings, setOpenSettings] = useState(false);

    const onGridReady = (params: GridReadyEvent) => {
        if (props.autofitColumns) {
            params.api.sizeColumnsToFit();
        }
        if (props.onGridReady) {
            props.onGridReady(params);
        }
    };

    const onColumnResize = (params: ColumnResizedEvent) => {
        if (props.onColumnResized) {
            props.onColumnResized(params);
        }
    };

    const onClick = (event: any) => {
        setOpenSettings(true);
    };

    return (
        <div
            className={'ag-theme-alpine ' + (props.gridClass || '')}
            style={props.gridStyle}
            data-testid={props.testId}>
            {props.displayToolbar && (
                <div className="action-bar">
                    {props.displayCreateNewButton && (
                        <Button
                            variant="text"
                            size="small"
                            className="grid-create-new"
                            onClick={props.onCreateNew}
                            startIcon={<AddCircleIcon />}>
                            Add New
                        </Button>
                    )}
                    {props.displayEntitySettings && (
                        <Button
                            variant="text"
                            size="small"
                            className="grid-create-new"
                            onClick={onClick}
                            startIcon={<SettingsIcon />}>
                            Settings
                        </Button>
                    )}
                </div>
            )}
            <AgGridReact
                ref={gridRef}
                domLayout={props.domLayout}
                immutableData={immutableData}
                getRowNodeId={dataKey ? (data) => data[dataKey] : null}
                defaultColDef={props.defaultColDef}
                gridOptions={props.gridOptions}
                tooltipShowDelay={props.tooltipShowDelay}
                pagination={props.pagination}
                paginationPageSize={props.paginationPageSize}
                cacheBlockSize={props.paginationPageSize}
                rowModelType={props.rowModelType}
                rowHeight={props.rowHeight}
                serverSideStoreType={props.serverSideStoreType}
                rowData={props.rowData || []}
                rowStyle={props.rowStyle}
                headerHeight={props.headerHeight}
                onFirstDataRendered={props.onFirstDataRendered}
                onSortChanged={props.onSortChanged}
                onFilterChanged={props.onFilterChanged}
                onGridReady={onGridReady}
                onColumnResized={onColumnResize}
                frameworkComponents={props.frameworkComponents}
                onCellClicked={props.onCellClicked}
                onRowClicked={props.onRowClicked}
                rowSelection={props.rowSelection}
                onRowSelected={props.onRowSelected}
                masterDetail={props.masterDetail}
                onPaginationChanged={props.onPaginationChanged}
                detailCellRendererParams={props.detailCellRendererParams}
                getRowStyle={props.getRowStyle}
                suppressMenuHide={props.suppressMenuHide}
                alwaysShowHorizontalScroll={props.alwaysShowHorizontalScroll}
                groupIncludeTotalFooter={props.groupIncludeTotalFooter}>
                {props.children}
            </AgGridReact>
            {props.displayEntitySettings && (
                <>
                    <EntitySettingsDrawer
                        open={openSettings}
                        setOpen={setOpenSettings}
                        settingKey={props.settingKey}
                        settingValue={gridRef}
                        type={'Grid'}
                        saveNewText={'Save New Grid Layout'}
                        currentSelectionText={'Your Current Grid Layout'}
                    />
                </>
            )}
        </div>
    );
};

Grid.defaultProps = {
    domLayout: GridDefaults.domLayout,
    tooltipShowDelay: GridDefaults.tooltipShowDelay,
    pagination: GridDefaults.pagination,
    paginationPageSize: GridDefaults.paginationPageSize,
    autofitColumns: GridDefaults.sizeColumnsToFit,
    immutableData: GridDefaults.immutableData,
    displayToolbar: GridDefaults.displayToolbar,
    alwaysShowHorizontalScroll: GridDefaults.alwaysShowHorizontalScroll,
};

export default Grid;
